import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  ExclamationTriangleIcon,
  TruckIcon,
  UserIcon,
} from '@heroicons/react/24/outline';
import { TimeDisplay } from '../schedule/TimeDisplay';
import { useCaseHelpers } from '../../hooks/useCaseHelpers';
import { formatDateForDisplay } from '../../utils/dateUtils';
import { CASE_STATUS_LABELS, REMARKS_LABELS } from '../../constants/dashboard';
import type { Case } from '../../types';

interface ManagementCaseDashboardProps {
  cases: Case[];
  selectedDate: string;
}

/**
 * 管理者・配車担当向けの案件管理ダッシュボード
 * 運営管理に必要な情報を効率的に表示
 */
export const ManagementCaseDashboard: React.FC<ManagementCaseDashboardProps> = ({
  cases,
  selectedDate,
}) => {
  const navigate = useNavigate();
  const {
    safe: {
      getVehicleName,
      getBillingPartnerName,
      getPickupPartnerName,
      getDeliveryPartnerName,
      getTransporterName,
    },
  } = useCaseHelpers();

  // ステータス別の案件分析
  const caseAnalysis = {
    urgent: cases.filter(c => c.remarks?.includes(REMARKS_LABELS.URGENT_KEYWORD)),
    unassigned: cases.filter(c => !c.assignedLandTransporterId), // FL-142: 未割当判定はassignedLandTransporterIdで判定
    inTransit: cases.filter(c => c.status === 'in_transit'),
    completed: cases.filter(c => c.status === 'completed'),
    all: cases,
  };

  // 案件がない場合の表示
  if (cases.length === 0) {
    return (
      <div className='text-center py-12'>
        <DocumentTextIcon className='mx-auto h-16 w-16 text-gray-400 dark:text-gray-600 mb-4' />
        <h3 className='text-lg font-medium text-gray-900 dark:text-white mb-2'>
          {formatDateForDisplay(selectedDate)}の案件はありません
        </h3>
        <p className='text-gray-500 dark:text-gray-400'>
          新規案件を作成するか、別の日付を選択してください
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {/* 案件概要ヘッダー */}
      <div className='bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6'>
        <div className='flex items-center justify-between mb-4'>
          <h3 className='text-lg font-semibold text-gray-900 dark:text-white flex items-center'>
            <DocumentTextIcon className='h-5 w-5 mr-2' />
            {formatDateForDisplay(selectedDate)}の案件管理
          </h3>
          <button
            onClick={() => navigate('/cases')}
            className='inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 shadow-sm text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors'
          >
            案件管理ページ
            <ArrowTopRightOnSquareIcon className='ml-2 h-4 w-4' />
          </button>
        </div>

        {/* ステータス統計 */}
        <div className='grid grid-cols-2 md:grid-cols-4 gap-4 mb-6'>
          <div className='text-center'>
            <div className='text-2xl font-bold text-gray-900 dark:text-white'>
              {caseAnalysis.all.length}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>総案件数</div>
          </div>
          <div className='text-center'>
            <div
              className={`text-2xl font-bold ${
                caseAnalysis.unassigned.length > 0
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-900 dark:text-white'
              }`}
            >
              {caseAnalysis.unassigned.length}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>未割当</div>
          </div>
          <div className='text-center'>
            <div className='text-2xl font-bold text-yellow-600 dark:text-yellow-400'>
              {caseAnalysis.inTransit.length}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>作業中</div>
          </div>
          <div className='text-center'>
            <div className='text-2xl font-bold text-green-600 dark:text-green-400'>
              {caseAnalysis.completed.length}
            </div>
            <div className='text-sm text-gray-600 dark:text-gray-400'>完了</div>
          </div>
        </div>

        {/* 進捗バー */}
        <div className='mb-4'>
          <div className='flex justify-between text-sm text-gray-600 dark:text-gray-400 mb-2'>
            <span>完了率</span>
            <span>
              {Math.round((caseAnalysis.completed.length / caseAnalysis.all.length) * 100)}%
            </span>
          </div>
          <div className='w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3'>
            <div
              className='bg-gradient-to-r from-green-500 to-green-600 h-3 rounded-full transition-all duration-300'
              style={{
                width: `${(caseAnalysis.completed.length / caseAnalysis.all.length) * 100}%`,
              }}
            />
          </div>
        </div>
      </div>

      {/* 緊急案件アラート */}
      {caseAnalysis.urgent.length > 0 && (
        <div className='bg-red-50 dark:bg-red-900/20 border-2 border-red-200 dark:border-red-800 rounded-lg p-4'>
          <div className='flex items-center mb-3'>
            <ExclamationTriangleIcon className='h-6 w-6 text-red-600 dark:text-red-400 mr-2' />
            <h4 className='text-lg font-semibold text-red-800 dark:text-red-200'>
              緊急案件 ({caseAnalysis.urgent.length}件)
            </h4>
          </div>
          <div className='space-y-3'>
            {caseAnalysis.urgent.slice(0, 3).map(case_ => (
              <div
                key={case_.id}
                onClick={() => navigate(`/cases/${case_.id}`)}
                className='bg-white dark:bg-red-900/10 rounded-lg p-3 cursor-pointer hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors border border-red-200 dark:border-red-700'
              >
                <div className='flex items-center justify-between'>
                  <div className='flex-1 min-w-0'>
                    <div className='font-medium text-red-800 dark:text-red-200 truncate'>
                      {getBillingPartnerName(case_)}
                    </div>
                    <div className='text-sm text-red-600 dark:text-red-300 mt-1'>
                      {getVehicleName(case_)}
                    </div>
                    <div className='mt-2'>
                      <TimeDisplay
                        pickupTimeStart={case_.pickupTimeStart}
                        pickupTimeEnd={case_.pickupTimeEnd}
                        deliveryTime={case_.deliveryTime}
                        textSize='text-xs'
                        spacing='space-y-0.5'
                      />
                    </div>
                  </div>
                  <div className='ml-4 text-right'>
                    <div
                      className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        {
                          provisional:
                            'bg-gray-200 dark:bg-gray-800 text-gray-800 dark:text-gray-200',
                          confirmed: 'bg-red-200 dark:bg-red-800 text-red-800 dark:text-red-200',
                          assigned: 'bg-blue-200 dark:bg-blue-800 text-blue-800 dark:text-blue-200',
                          picked_up:
                            'bg-indigo-200 dark:bg-indigo-800 text-indigo-800 dark:text-indigo-200',
                          in_transit:
                            'bg-yellow-200 dark:bg-yellow-800 text-yellow-800 dark:text-yellow-200',
                          delivered:
                            'bg-purple-200 dark:bg-purple-800 text-purple-800 dark:text-purple-200',
                          reported: 'bg-teal-200 dark:bg-teal-800 text-teal-800 dark:text-teal-200',
                          completed:
                            'bg-green-200 dark:bg-green-800 text-green-800 dark:text-green-200',
                        }[case_.status]
                      }`}
                    >
                      {CASE_STATUS_LABELS[case_.status]}
                    </div>
                  </div>
                </div>
              </div>
            ))}
            {caseAnalysis.urgent.length > 3 && (
              <div className='text-center text-sm text-red-600 dark:text-red-400'>
                他{caseAnalysis.urgent.length - 3}件の緊急案件があります
              </div>
            )}
          </div>
        </div>
      )}

      {/* 未割当案件アラート */}
      {caseAnalysis.unassigned.length > 0 && (
        <div className='bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4'>
          <div className='flex items-center justify-between mb-3'>
            <h4 className='font-semibold text-orange-800 dark:text-orange-200 flex items-center'>
              <UserIcon className='h-5 w-5 mr-2' />
              未割当案件 ({caseAnalysis.unassigned.length}件)
            </h4>
            <button
              onClick={() => navigate('/cases?status=unassigned')}
              className='text-sm text-orange-600 dark:text-orange-400 hover:text-orange-800 dark:hover:text-orange-200'
            >
              配車する →
            </button>
          </div>
          <div className='text-sm text-orange-700 dark:text-orange-300'>
            陸送員の割り当てが必要な案件があります
          </div>
        </div>
      )}

      {/* 案件一覧（管理者向け詳細表示） */}
      <div className='bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700'>
        <div className='px-6 py-4 border-b border-gray-200 dark:border-gray-700'>
          <h4 className='text-lg font-medium text-gray-900 dark:text-white'>
            本日の案件一覧 (最新5件)
          </h4>
        </div>
        <div className='divide-y divide-gray-200 dark:divide-gray-700'>
          {cases.slice(0, 5).map(case_ => (
            <div
              key={case_.id}
              onClick={() => navigate(`/cases/${case_.id}`)}
              className='p-6 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors'
            >
              <div className='flex items-center justify-between'>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center space-x-3 mb-2'>
                    <h5 className='text-lg font-medium text-gray-900 dark:text-white truncate'>
                      {getBillingPartnerName(case_)}
                    </h5>
                    <div
                      className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                        {
                          provisional:
                            'bg-gray-100 dark:bg-gray-900/30 text-gray-800 dark:text-gray-400',
                          confirmed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400',
                          assigned:
                            'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400',
                          picked_up:
                            'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-800 dark:text-indigo-400',
                          in_transit:
                            'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-400',
                          delivered:
                            'bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400',
                          reported:
                            'bg-teal-100 dark:bg-teal-900/30 text-teal-800 dark:text-teal-400',
                          completed:
                            'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400',
                        }[case_.status]
                      }`}
                    >
                      {CASE_STATUS_LABELS[case_.status]}
                    </div>
                    {case_.remarks?.includes(REMARKS_LABELS.URGENT_KEYWORD) && (
                      <ExclamationTriangleIcon className='h-5 w-5 text-red-500' />
                    )}
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-3 gap-4 text-sm'>
                    <div className='flex items-center space-x-2'>
                      <TruckIcon className='h-4 w-4 text-gray-400 dark:text-gray-500 flex-shrink-0' />
                      <span className='text-gray-600 dark:text-gray-400'>
                        {getVehicleName(case_)}
                      </span>
                    </div>
                    <div>
                      <TimeDisplay
                        pickupTimeStart={case_.pickupTimeStart}
                        pickupTimeEnd={case_.pickupTimeEnd}
                        deliveryTime={case_.deliveryTime}
                        textSize='text-sm'
                        spacing='space-y-1'
                      />
                    </div>
                  </div>

                  <div className='grid grid-cols-1 md:grid-cols-2 gap-4 text-sm mt-2'>
                    <div className='text-gray-600 dark:text-gray-400'>
                      <span className='font-medium'>引取先:</span> {getPickupPartnerName(case_)}
                    </div>
                    <div className='text-gray-600 dark:text-gray-400'>
                      <span className='font-medium'>搬入先:</span> {getDeliveryPartnerName(case_)}
                    </div>
                  </div>

                  {case_.assignedLandTransporterId && (
                    <div className='flex items-center space-x-2 mt-2 text-sm'>
                      <UserIcon className='h-4 w-4 text-indigo-500 flex-shrink-0' />
                      <span className='text-indigo-600 dark:text-indigo-400 font-medium'>
                        担当: {getTransporterName(case_.assignedLandTransporterId)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
        {cases.length > 5 && (
          <div className='px-6 py-4 border-t border-gray-200 dark:border-gray-700 text-center'>
            <button
              onClick={() => navigate('/cases')}
              className='text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 font-medium'
            >
              すべての案件を見る ({cases.length - 5}件)
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
